import React, { useState, useEffect, useCallback } from 'react';
import { Package, Truck, ArrowLeftRight, Eye, Plus, RotateCcw, Loader2, CheckCircle2, Download, Check } from 'lucide-react';
import { materialRequestService } from '../services/materialRequestService';
import { materialReturnService } from '../services/materialReturnService';
import { RequestMaterialsModal } from '../modals/RequestMaterialsModal';
import { ReturnMaterialsModal } from '../modals/ReturnMaterialsModal';
import { MaterialRequestDetailsModal } from '../modals/MaterialRequestDetailsModal';
import { MaterialReturnDetailsModal } from '../modals/MaterialReturnDetailsModal';
import { ReceiveMaterialModal } from '../modals/ReceiveMaterialModal';
import DataTable from '@/components/ui/DataTable';
import Pagination from '@/components/ui/Pagination';
import Toast from '@/components/ui/Toast';
import { hasProjectInstallationTeam } from '../utils/projectTeamAccess';
import { formatInstallationTrade } from '../../verve-teams/utils/teamMemberLabels';
import { NormalSearch } from '../../client_contact_stages/Initial-Quotation/components/NormalSearch';

import { getMaterialRequestByLeadId } from '../../../services/VerveMaterialRequestService';
import { HardwareSelectionTable } from '../../client_contact_stages/Initial-Quotation/components/HardwareSelectionTable';
const assignedMemberLabel = (req) => {
  const u = req?.assignedTo;
  if (!u || typeof u !== 'object') return '—';
  return u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—';
};

const returnSubmitterRoleLabel = (role) => {
  if (!role) return '';
  const map = {
    'designer': 'Designer',
    installation_team: 'Installation member',
    owner: 'Owner',
    admin: 'Admin',
    accountant: 'Accountant',
    'store_incharge': 'Store Incharge'
  };
  return map[role] || String(role).replace(/_/g, ' ');
};

export const ProjectInventoryTab = ({ 
   project, 
   tasksInventoryLocked = false, 
   viewerRole,
   hideHeader = false,
   onUpdate
}) => {
   const [requests, setRequests] = useState([]);
   const [returns, setReturns] = useState([]);
   const [loading, setLoading] = useState(false);
   const [returnsLoading, setReturnsLoading] = useState(false);
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
   const [isDetailsOpen, setIsDetailsOpen] = useState(false);
   const [isReturnDetailsOpen, setIsReturnDetailsOpen] = useState(false);
   const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
   const [selectedRequest, setSelectedRequest] = useState(null);
   const [selectedReturn, setSelectedReturn] = useState(null);
   const [toast, setToast] = useState(null);
   
   const [hardwareItems, setHardwareItems] = useState([]);

   const [pagination, setPagination] = useState({
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0
   });
   const [returnPagination, setReturnPagination] = useState({
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0
   });

   const fetchRequests = useCallback(async () => {
      if (!project?._id) return;
      setLoading(true);
      try {
         const res = await materialRequestService.getProjectRequests(project._id, pagination.page, pagination.limit);
         const data = res?.data?.data || res?.data || res;
         if (data && data.pagination) {
            setRequests(data.requests || []);
            setPagination(prev => ({ 
               ...prev, 
               total: data.pagination.total || 0, 
               totalPages: data.pagination.totalPages || 0
            }));
         }
      } catch (error) {
         console.error('Error fetching material requests:', error);
      } finally {
         setLoading(false);
      }
   }, [project?._id, pagination.page, pagination.limit]);

   const fetchReturns = useCallback(async () => {
      if (!project?._id) return;
      setReturnsLoading(true);
      try {
         const res = await materialReturnService.getProjectReturns(project._id, returnPagination.page, returnPagination.limit);
         const data = res?.data?.data || res?.data || res;
         if (data && data.pagination) {
            setReturns(data.returns || []);
            setReturnPagination(prev => ({ 
               ...prev, 
               total: data.pagination.total || 0, 
               totalPages: data.pagination.totalPages || 0
            }));
         }
      } catch (error) {
         console.error('Error fetching material returns:', error);
      } finally {
         setReturnsLoading(false);
      }
   }, [project?._id, returnPagination.page, returnPagination.limit]);

   useEffect(() => {
     const fetchInitialMaterials = async () => {
       try {
         const leadId = project?.lead?._id || project?.lead || project?._id;
         if (leadId) {
           const res = await getMaterialRequestByLeadId(leadId);
           if (res && res.data && res.data.items) {
             setHardwareItems(res.data.items.map(item => ({
               tempId: item._id || Date.now() + Math.random(),
               name: item.description || item.itemName || item.name || 'Material Item',
               amount: item.netAmount || item.vervePrice || item.unitPrice || item.amount || 0,
               _id: item._id,
               rawData: item
             })));
           }
         }
       } catch (error) {
         console.error('Error fetching initial materials:', error);
       }
     };
     fetchInitialMaterials();
   }, [project?.lead, project?._id]);

   useEffect(() => {
      fetchRequests();
      fetchReturns();
   }, [fetchRequests, fetchReturns]);

   const handleExportMaterials = async () => {
      try {
         setLoading(true);
         const res = await materialRequestService.getProjectRequests(project._id, 1, 5000);
         const data = res?.data?.data || res?.data || res;
         const allRequests = data?.requests || [];
         
         const headers = [
            'Client Name', 'Project ID', 'Customer Project ID', 'Site Name', 'Customer',
            'Vendor Name', 'Request No', 'Date', 'Department', 'Assigned To',
            'Category', 'Item Name', 'Quantity', 'Delivered Qty', 'Unit Price', 'Total Amount',
            'Driver Name', 'Vehicle Number', 'Dispatched Date', 'Request Notes', 'Approval Notes', 
            'Dispatch Notes', 'Receive Notes', 'Status'
         ];
         
         const rows = [];
         allRequests.forEach(req => {
            const reqDate = req.createdAt ? new Date(req.createdAt).toLocaleDateString('en-GB') : '-';
            const dept = req.installationTrade ? formatInstallationTrade(req.installationTrade) : 'General';
            const status = String(req.status || 'Pending').toUpperCase();
            
            const assigneeName = req.assignedTo ? (req.assignedTo.name || `${req.assignedTo.firstName || ''} ${req.assignedTo.lastName || ''}`.trim()) : '';
            const assignee = assigneeName || (typeof req.assignedTo === 'string' ? req.assignedTo : 'Unassigned');
            
            const requesterName = req.requestedBy ? (req.requestedBy.name || `${req.requestedBy.firstName || ''} ${req.requestedBy.lastName || ''}`.trim()) : '';
            const requester = requesterName || (typeof req.requestedBy === 'string' ? req.requestedBy : '-');
            
            const driverName = req.dispatchDetails?.driverName || '-';
            const vehicleNumber = req.dispatchDetails?.vehicleNumber || '-';
            const dispatchedDate = req.dispatchDetails?.dispatchedAt ? new Date(req.dispatchDetails.dispatchedAt).toLocaleDateString('en-GB') : '-';
            
            const clientName = project?.customerName || '-';
            const projectId = project?.projectId || '-';
            const customerProjectId = project?.applicationId || '-';
            const siteName = project?.address?.siteName || project?.address?.city || '-';
            const customer = project?.projectName || '-';
            
            const requestNotes = req.notes?.replace(/"/g, '""') || '-';
            const approvalNotes = req.approvalNotes?.replace(/"/g, '""') || '-';
            const dispatchNotes = req.dispatchDetails?.notes?.replace(/"/g, '""') || '-';
            const receiveNotes = req.receiveDetails?.statusNotes?.replace(/"/g, '""') || '-';
            
            (req.items || []).forEach(item => {
               const itemName = item.itemName || '';
               const category = item.inventoryId?.item?.category?.categoryName || (typeof item.inventoryId?.item?.category === 'string' ? item.inventoryId?.item?.category : '');
               const qty = item.quantity || 0;
               const delivered = item.deliveredQuantity || 0;
               const unitPrice = item.inventoryId?.unitPrice || 0;
               const itemTotal = unitPrice * qty;
               const vendor = item.inventoryId?.vendor?.vendorName || (typeof item.inventoryId?.vendor === 'string' ? item.inventoryId?.vendor : '');
               
               rows.push([
                  `"${clientName}"`,
                  `"${projectId}"`,
                  `"${customerProjectId}"`,
                  `"${siteName}"`,
                  `"${customer}"`,
                  `"${vendor}"`,
                  req.requestNo || '-',
                  reqDate,
                  `"${dept}"`,
                  `"${assignee}"`,
                  `"${category}"`,
                  `"${itemName}"`,
                  qty,
                  delivered,
                  unitPrice,
                  itemTotal,
                  `"${driverName}"`,
                  `"${vehicleNumber}"`,
                  dispatchedDate,
                  `"${requestNotes}"`,
                  `"${approvalNotes}"`,
                  `"${dispatchNotes}"`,
                  `"${receiveNotes}"`,
                  status
               ]);
            });
         });
         
         const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
         const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
         const url = URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.setAttribute('href', url);
         link.setAttribute('download', `Project_${project?.projectId || 'Materials'}_Costs.csv`);
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
      } catch (err) {
         setToast({ message: 'Error exporting materials', type: 'error' });
      } finally {
         setLoading(false);
      }
   };

   const handleExportReturns = async () => {
      try {
         setReturnsLoading(true);
         const res = await materialReturnService.getProjectReturns(project._id, 1, 5000);
         const data = res?.data?.data || res?.data || res;
         const allReturns = data?.returns || [];
         
         const headers = [
            'Client Name', 'Project ID', 'Customer Project ID', 'Site Name', 'Customer',
            'Vendor Name', 'Return No', 'Date', 'Requested By',
            'Category', 'Item Name', 'Quantity', 'Reason',
            'Driver Name', 'Vehicle Number', 'Transport Mode', 'Tracking Number', 
            'Return Notes', 'Status'
         ];
         
         const rows = [];
         allReturns.forEach(ret => {
            const retDate = ret.createdAt ? new Date(ret.createdAt).toLocaleDateString('en-GB') : '-';
            const status = String(ret.status || 'Pending').toUpperCase();
            
            const requesterName = ret.requestedBy ? (ret.requestedBy.name || `${ret.requestedBy.firstName || ''} ${ret.requestedBy.lastName || ''}`.trim()) : '';
            const requester = requesterName || (typeof ret.requestedBy === 'string' ? ret.requestedBy : '-');
            
            const driverName = ret.driverName || '-';
            const vehicleNumber = ret.vehicleNumber || '-';
            const transportMode = ret.transportMode || '-';
            const trackingNumber = ret.trackingNumber || '-';
            const returnNotes = ret.notes?.replace(/"/g, '""') || '-';
            
            const clientName = project?.customerName || '-';
            const projectId = project?.projectId || '-';
            const customerProjectId = project?.applicationId || '-';
            const siteName = project?.address?.siteName || project?.address?.city || '-';
            const customer = project?.projectName || '-';
            const vendorName = ret.vendor?.vendorName || (typeof ret.vendor === 'string' ? ret.vendor : '-');
            
            (ret.items || []).forEach(item => {
               const itemName = item.itemName || '';
               const category = item.inventoryId?.item?.category?.categoryName || (typeof item.inventoryId?.item?.category === 'string' ? item.inventoryId?.item?.category : '');
               const qty = item.quantity || 0;
               const reason = item.reason || '-';
               
               rows.push([
                  `"${clientName}"`,
                  `"${projectId}"`,
                  `"${customerProjectId}"`,
                  `"${siteName}"`,
                  `"${customer}"`,
                  `"${vendorName}"`,
                  ret.returnNo || '-',
                  retDate,
                  `"${requester}"`,
                  `"${category}"`,
                  `"${itemName}"`,
                  qty,
                  `"${reason}"`,
                  `"${driverName}"`,
                  `"${vehicleNumber}"`,
                  `"${transportMode}"`,
                  `"${trackingNumber}"`,
                  `"${returnNotes}"`,
                  status
               ]);
            });
         });
         
         const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
         const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
         const url = URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.setAttribute('href', url);
         link.setAttribute('download', `Project_${project?.projectId || 'Returns'}_Export.csv`);
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
      } catch (err) {
         setToast({ message: 'Error exporting returns', type: 'error' });
      } finally {
         setReturnsLoading(false);
      }
   };

   const handleReceiveConfirm = async (payload) => {
      try {
         await materialRequestService.bulkReceiveRequest(selectedRequest._id, payload);
         setToast({ message: 'Materials received on site', type: 'success' });
         fetchRequests();
         if (onUpdate) onUpdate();
      } catch (error) {
         setToast({ message: error.message || 'Error receiving materials', type: 'error' });
      }
   };     const requestColumns = [
      {
         header: 'Request No',
         accessor: (row) => (
            <span className="text-[10px] font-bold text-brand-orange whitespace-nowrap">
               #{row.requestNo || "—"}
            </span>
         )
      },
      {
         header: 'Project ID',
         headerClassName: 'whitespace-nowrap',
         cellClassName: 'whitespace-nowrap',
         accessor: (row) => (
            <span className="text-[10px] text-brand-blue font-bold uppercase tracking-wider whitespace-nowrap">
               {row.project?.applicationId || project?.applicationId || row.project?.projectId || project?.projectId || "—"}
            </span>
         )
      },
      {
         header: 'Customer Name',
         accessor: (row) => (
            <span className="text-brand-dark tracking-tight text-[12px] font-medium leading-tight truncate">
               {row.project?.customerName || "—"}
            </span>
         )
      },
      {
         header: 'Department',
         accessor: (row) => (
            <span className="text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-slate-100 text-slate-600">
               {row.installationTrade ? formatInstallationTrade(row.installationTrade) : 'General'}
            </span>
         )
      },
      {
         header: 'Items Count',
         accessor: (row) => (
            <span className="text-[12px] font-semibold text-slate-700">
               {row.items?.length || 0} items
            </span>
         )
      },
      {
         header: 'Date',
         headerClassName: 'whitespace-nowrap',
         cellClassName: 'whitespace-nowrap',
         accessor: (row) => (
            <span className="text-[11px] font-medium text-slate-500 tabular-nums whitespace-nowrap">
               {new Date(row.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
         )
      },
      {
         header: 'Requested By',
         accessor: (row) => {
            const reqUser = row.requestedBy || {};
            const reqName = reqUser.name || `${reqUser.firstName || ''} ${reqUser.lastName || ''}`.trim() || 'Staff';
            return (
               <span className="text-[11px] font-bold text-slate-600 uppercase whitespace-nowrap">
                  {reqName}
               </span>
            );
         }
      },
      {
         header: 'Status',
         accessor: (row) => {
            const s = row.status || "PENDING";
            const colors = {
               PENDING: "bg-amber-100 text-amber-700 border-amber-200",
               AWAITING_OWNER_APPROVAL: "bg-blue-100 text-blue-700 border-blue-200",
               APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
               DISPATCHED: "bg-cyan-100 text-cyan-700 border-cyan-200",
               DELIVERED: "bg-emerald-100 text-emerald-700 border-emerald-200",
               REJECTED: "bg-red-100 text-red-700 border-red-200",
            };
            return (
               <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold uppercase tracking-wider border whitespace-nowrap ${colors[s] || colors.PENDING}`}>
                  {s.replace(/_/g, " ")}
               </span>
            );
         }
      },
      {
         header: 'Actions',
         headerClassName: 'text-right w-[120px]',
         cellClassName: 'w-[120px] text-right align-top',
         accessor: (row) => {
            const isDispatched = row.status === 'DISPATCHED' || row.status === 'PARTIAL';
            const isPending = row.status === 'PENDING';
            const canReceive = ['installation_team', 'designer', 'owner', 'admin'].includes(viewerRole);
            
            return (
               <div className="flex items-center justify-end gap-1">
                  <button
                     type="button"
                     onClick={() => {
                        setSelectedRequest(row);
                        setIsDetailsOpen(true);
                      }}
                      className="inline-flex cursor-pointer items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold text-slate-500 capitalize tracking-tight transition-all whitespace-nowrap hover:bg-slate-50"
                  >
                     <Eye className="w-3.5 h-3.5 shrink-0" />
                  </button>
               </div>
            );
         }
      }
   ];

   const returnColumns = [
      {
         header: 'Items',
         headerClassName: 'min-w-[200px]',
         cellClassName: 'min-w-[200px] align-top',
         accessor: (row) => {
            const qtyLine = row.items.map((i) => `${i.quantity} units`).join(', ');
            return (
               <div className="flex flex-col gap-0.5 min-w-0 max-w-full">
                  <span className="text-[12px] font-semibold text-slate-700 tracking-tight line-clamp-2 break-words">
                     {row.items[0]?.itemName} {row.items.length > 1 && `+${row.items.length - 1} more`}
                  </span>
                  <span className="text-[9px] font-medium text-slate-400 capitalize tracking-tight line-clamp-2 break-all">
                     {qtyLine.length > 48 ? `${qtyLine.slice(0, 48)}…` : qtyLine}
                  </span>
               </div>
            );
         }
      },
      {
         header: 'Driver / Vehicle',
         headerClassName: 'w-[160px]',
         cellClassName: 'w-[160px] align-top',
         accessor: (row) => (
            <div className="flex flex-col gap-0.5 min-w-0">
               <span className="text-[11px] font-semibold text-slate-700 truncate">{row.driverName || '—'}</span>
               <span className="text-[9px] font-medium text-slate-400 uppercase tracking-tight truncate">{row.vehicleNumber || '—'}</span>
            </div>
         )
      },
      {
         header: 'Amount',
         headerClassName: 'w-[100px]',
         cellClassName: 'w-[100px] align-top',
         accessor: (row) => (
            row.totalAmount > 0 ? (
               <span className="text-[11px] font-bold text-blue-600 block truncate">
                  ₹{row.totalAmount.toLocaleString('en-IN')}
               </span>
            ) : <span className="text-[11px] text-slate-400 block truncate">—</span>
         )
      },
      {
         header: 'Date',
         headerClassName: 'w-[120px]',
         cellClassName: 'w-[120px] whitespace-nowrap align-top',
         accessor: (row) => (
            <span className="text-[11px] font-medium text-slate-500 tabular-nums block truncate">
               {new Date(row.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
         )
      },
      {
         header: 'Status',
         headerClassName: 'w-[160px]',
         cellClassName: 'w-[160px] align-top',
         accessor: (row) => {
            const status = String(row.status || '').toUpperCase();
            let dotColor = 'bg-slate-300';
            let textColor = 'text-slate-500';
            let displayText = status.toLowerCase();

            if (status === 'PENDING') {
               dotColor = 'bg-amber-400';
               textColor = 'text-amber-500';
               displayText = 'Pending';
            } else if (status === 'RECEIVED') {
               dotColor = 'bg-emerald-400';
               textColor = 'text-emerald-600';
               displayText = 'Received';
            } else if (status === 'RESTOCKED') {
               dotColor = 'bg-blue-400';
               textColor = 'text-blue-600';
               displayText = 'Restocked';
            } else if (status === 'CANCELLED') {
               dotColor = 'bg-rose-400';
               textColor = 'text-rose-500';
               displayText = 'Cancelled';
            } else if (status === 'REJECTED') {
               dotColor = 'bg-rose-400';
               textColor = 'text-rose-500';
               displayText = 'Rejected';
            }

            return (
               <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />
                  <span className={`text-[10px] font-semibold capitalize tracking-tight truncate ${textColor}`}>
                     {displayText}
                  </span>
               </div>
            );
         }
      },
      {
         header: 'Action',
         headerClassName: 'text-right w-[80px]',
         cellClassName: 'w-[80px] text-right align-top',
         accessor: (row) => (
            <button
               type="button"
               onClick={() => {
                  setSelectedReturn(row);
                  setIsReturnDetailsOpen(true);
               }}
               className="inline-flex cursor-pointer items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold text-slate-500 capitalize tracking-tight transition-all whitespace-nowrap hover:bg-slate-50"
            >
               <Eye className="w-3.5 h-3.5 shrink-0" />
            </button>
         )
      }
   ];

   const isRequestDisabled = false; // User requested to enable this in all projects
   const logisticsActionsDisabled = isRequestDisabled || tasksInventoryLocked;
   const canRequestMaterials = viewerRole !== 'installation_team';

   return (
      <div className="flex flex-col animate-in fade-in slide-in-from-bottom duration-500">
         {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
         
         <div className="flex flex-col gap-5">
            <div className="bg-white border border-slate-200 rounded-xl overflow-visible flex flex-col">
               <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/20 rounded-t-xl">
                  <div className="flex items-center gap-3">
                     <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <Truck className="w-4 h-4 text-[#2b448f]" />
                     </div>
                     <div className="flex flex-col">
                        <span className="text-[11px] font-bold text-[#2b448f] capitalize tracking-tight">Outgoing material requests</span>
                        <span className="text-[9px] font-medium text-slate-400 capitalize tracking-tight">Track material fulfillment status</span>
                     </div>
                  </div>
                  <div className="flex items-center gap-3">
                     {pagination.total > 0 && (
                        <span className="px-2.5 py-1 rounded bg-white text-slate-500 text-[9px] font-bold border border-slate-100 tracking-tight flex items-center gap-1.5 shadow-none">
                           <div className="w-1 h-1 rounded-full bg-slate-300" />
                           {pagination.total} requests total
                        </span>
                     )}
                     <button
                        onClick={handleExportMaterials}
                        className="flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold tracking-wider transition-all shadow-sm bg-transparent border border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                     >
                        <Download className="w-3.5 h-3.5" />
                        Export All
                     </button>
                     {canRequestMaterials && (
                        <button
                           type="button"
                           id="project-inventory-request-materials"
                           onClick={() => !logisticsActionsDisabled && setIsModalOpen(true)}
                           disabled={logisticsActionsDisabled}
                           className={`flex items-center gap-2 px-4 py-2 rounded-md text-[11px] font-semibold cursor-pointer tracking-wider transition-all ${
                               logisticsActionsDisabled 
                                  ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed' 
                                  : 'bg-white text-[#2b448f] border border-[#2b448f] hover:bg-[#2b448f]/5'
                            }`}
                        >
                           <Plus className="w-3.5 h-3.5" />
                           Request Materials
                        </button>
                     )}
                  </div>
               </div>
               <DataTable
                  columns={requestColumns}
                  data={requests}
                  isLoading={loading}
                  tableClassName="min-w-0 w-full"
                  wrapperClassName=""
               />

               {pagination.totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-slate-50 flex justify-end bg-slate-50/10">
                     <Pagination 
                        page={pagination.page} 
                        totalPages={pagination.totalPages} 
                        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))} 
                     />
                  </div>
               )}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col">
               <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-blue-50/5">
                  <div className="flex items-center gap-3">
                     <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <RotateCcw className="w-4 h-4 text-blue-500" />
                     </div>
                     <div className="flex flex-col">
                        <span className="text-[11px] font-bold text-blue-600 capitalize tracking-tight">Returned material requests</span>
                        <span className="text-[9px] font-medium text-slate-400 capitalize tracking-tight">Manage sent back site materials</span>
                     </div>
                  </div>
                  <div className="flex items-center gap-3">
                     {returnPagination.total > 0 && (
                        <span className="px-2.5 py-1 rounded bg-white text-slate-500 text-[9px] font-bold border border-slate-100 tracking-tight flex items-center gap-1.5 shadow-none">
                           <div className="w-1 h-1 rounded-full bg-slate-300" />
                           {returnPagination.total} returns total
                        </span>
                     )}
                     <button
                        onClick={handleExportReturns}
                        className="flex items-center cursor-pointer gap-2 px-4 py-2 rounded-md text-[11px] font-semibold tracking-wider transition-all shadow-sm bg-transparent border border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                     >
                        <Download className="w-3.5 h-3.5" />
                        Export All
                     </button>
                     <button 
                        onClick={() => !logisticsActionsDisabled && setIsReturnModalOpen(true)}
                        disabled={logisticsActionsDisabled}
                         className={`flex items-center gap-2 px-4 py-2 border rounded-md text-[11px] font-semibold cursor-pointer tracking-wider transition-all ${
                            logisticsActionsDisabled 
                               ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' 
                               : 'bg-white text-[#2b448f] border-[#2b448f] hover:bg-[#2b448f]/5'
                         }`}
                     >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Return Material
                     </button>
                  </div>
               </div>

               <DataTable
                  columns={returnColumns}
                  data={returns}
                  isLoading={returnsLoading}
                  tableClassName="min-w-0 w-full"
                  wrapperClassName=""
               />

               {returnPagination.totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-slate-50 flex justify-end bg-slate-50/10">
                     <Pagination 
                        page={returnPagination.page} 
                        totalPages={returnPagination.totalPages} 
                        onPageChange={(page) => setReturnPagination(prev => ({ ...prev, page }))} 
                     />
                  </div>
               )}
            </div>
         </div>

         {/* Modals */}
         {canRequestMaterials && (
            <RequestMaterialsModal
               isOpen={isModalOpen}
               onClose={() => setIsModalOpen(false)}
               project={project}
               viewerRole={viewerRole}
               source="overview"
               onSuccess={() => {
                  fetchRequests();
                  fetchReturns();
               }}
            />
         )}

         <ReturnMaterialsModal
            isOpen={isReturnModalOpen}
            onClose={() => setIsReturnModalOpen(false)}
            project={project}
            setToast={setToast}
            onSuccess={() => {
               fetchRequests();
               fetchReturns();
               setToast({ message: 'Return request submitted', type: 'success' });
            }}
         />

         <MaterialRequestDetailsModal 
            isOpen={isDetailsOpen}
            onClose={() => {
               setIsDetailsOpen(false);
               setSelectedRequest(null);
            }}
            request={selectedRequest}
         />

         <ReceiveMaterialModal 
            isOpen={isReceiveModalOpen}
            onClose={() => {
               setIsReceiveModalOpen(false);
               setSelectedRequest(null);
            }}
            request={selectedRequest}
            onConfirm={handleReceiveConfirm}
         />

         <MaterialReturnDetailsModal 
            isOpen={isReturnDetailsOpen}
            onClose={() => {
               setIsReturnDetailsOpen(false);
               setSelectedReturn(null);
            }}
            returnData={selectedReturn}
         />
      </div>
   );
};
